import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, Grid3x3, PhoneOff, Volume2 } from "lucide-react";
import { Waveform } from "./Waveform";

const transcript = [
  { who: "AI", text: "Thanks for calling OwnPBX — how can I help?" },
  { who: "Caller", text: "Hi, I want to set up an AI agent for my sales team." },
  { who: "AI", text: "Great — I can start a Pro AI trial and connect your number today." },
  { who: "Caller", text: "Perfect, let's do it." },
  { who: "AI", text: "Done! Your agent is live. I've emailed the setup steps." },
];

export const CallWidget = () => {
  const [count, setCount] = useState(1);
  const [secs, setSecs] = useState(47);

  useEffect(() => {
    const t = setInterval(() => setSecs((s) => s + 1), 1000);
    const id = setInterval(() => setCount((c) => (c >= transcript.length ? 1 : c + 1)), 2000);
    return () => { clearInterval(t); clearInterval(id); };
  }, []);

  const mmss = `${String(Math.floor(secs / 60)).padStart(2, "0")}:${String(secs % 60).padStart(2, "0")}`;
  const shown = transcript.slice(0, count);

  return (
    <div className="relative bg-[#0a0a0a] border border-black shadow-[0_30px_80px_-20px_rgba(0,0,0,0.4)]" data-testid="web-call-widget">
      {/* faux browser bar */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10">
        <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
        <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
        <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
        <span className="ml-3 flex-1 text-center font-mono-t text-[11px] text-neutral-500 bg-white/5 py-1">yourbusiness.com</span>
      </div>

      {/* faux page content */}
      <div className="relative p-6 min-h-[380px]">
        <div className="space-y-2 opacity-30">
          <div className="h-5 w-1/2 bg-white/20" />
          <div className="h-2.5 w-4/5 bg-white/10" />
          <div className="h-2.5 w-3/5 bg-white/10" />
          <div className="mt-4 h-16 w-full bg-white/5 border border-white/10" />
        </div>

        {/* call widget */}
        <div className="absolute bottom-5 right-5 w-[290px] bg-white text-neutral-900 border border-black/10 shadow-2xl">
          {/* header */}
          <div className="flex items-center justify-between px-3.5 py-3 border-b border-black/10">
            <div className="flex items-center gap-2">
              <span className="grid place-items-center w-7 h-7 bg-neutral-900 text-white text-[11px] font-bold font-heading">AI</span>
              <div className="leading-tight">
                <p className="text-[12px] font-semibold">OwnPBX Agent</p>
                <p className="text-[10px] text-neutral-500 flex items-center gap-1 font-mono-t">
                  <span className="w-1.5 h-1.5 rounded-full bg-neutral-900 animate-pulse" /> Live call · {mmss}
                </p>
              </div>
            </div>
            <Volume2 size={15} className="text-neutral-400" />
          </div>

          {/* flowing audio */}
          <div className="px-3.5 py-3 border-b border-black/10">
            <Waveform bars={34} height={38} color="#0a0a0a" />
          </div>

          {/* live transcription */}
          <div className="px-3.5 py-3 min-h-[150px] flex flex-col justify-end">
            <p className="font-mono-t text-[9px] uppercase tracking-widest text-neutral-400 mb-2">Live transcription</p>
            <div className="space-y-1.5 font-mono-t text-[11.5px] leading-snug">
              <AnimatePresence initial={false}>
                {shown.map((l, i) => (
                  <motion.div
                    key={`${count}-${i}`}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="flex gap-2"
                  >
                    <span className={`shrink-0 uppercase text-[9px] tracking-wider pt-[2px] ${l.who === "AI" ? "text-neutral-900 font-semibold" : "text-neutral-400"}`}>
                      {l.who}
                    </span>
                    <span className={l.who === "AI" ? "text-neutral-800" : "text-neutral-500"}>{l.text}</span>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>

          {/* controls */}
          <div className="flex items-center justify-center gap-3 px-3.5 py-3 border-t border-black/10">
            <button className="grid place-items-center w-9 h-9 border border-black/15 hover:bg-neutral-100 transition-colors" aria-label="Mute">
              <Mic size={15} />
            </button>
            <button className="grid place-items-center w-9 h-9 border border-black/15 hover:bg-neutral-100 transition-colors" aria-label="Keypad">
              <Grid3x3 size={15} />
            </button>
            <button className="grid place-items-center w-9 h-9 bg-neutral-900 text-white hover:bg-neutral-700 transition-colors" aria-label="End call">
              <PhoneOff size={15} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
